import React, {
    useCallback, useContext, useEffect, useMemo, useRef, useState
} from 'react';
import cn from 'classnames';
import { Input } from '@ferris/react/components/forms';
import { Button, Loader } from '@ferris/react';
import { CompetitorUrlContext, CALL_STATUS, CallStatusContext } from '../Context';
import { BaseComponentProptypes } from '../properties';

function BaseComponent(props) {
    const {
        error,
        loading,
        callStatus,
        executeCall,
        executeRemove
    } = useContext(CallStatusContext);
    const {
        setCompetitorUrl,
        competitorUrl
    } = useContext(CompetitorUrlContext);
    const {
        placeholder,
        ctaConfirm,
        ctaCancel,
        title,
        description
    } = props;

    const EXAMPLE_URL = 'https://www.booking.com/hotel/us/east-austin';
    const [isFocused, setIsFocused] = useState(false);

    const inputHandler = useCallback(({ target: { value } }) => {
        // Clean up common copy-paste issues
        let cleanedValue = value;

        // Remove extra whitespace and newlines common in copy-paste
        cleanedValue = cleanedValue.replace(/\s+/g, ' ').trim();

        // Auto-add https:// if it looks like a domain but missing protocol
        if (cleanedValue
            && !cleanedValue.startsWith('http://')
            && !cleanedValue.startsWith('https://')
            && (cleanedValue.startsWith('www.') || /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}/.test(cleanedValue))) {
            cleanedValue = `https://${cleanedValue}`;
        }

        setCompetitorUrl(cleanedValue);
    }, [setCompetitorUrl]);

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);
    };

    const getInputLabel = () => {
        // Show placeholder prop when focused or when input has value
        if (isFocused || competitorUrl) {
            return placeholder;
        }
        // Show example URL as label when input is empty and not focused
        return EXAMPLE_URL;
    };

    // button handlers
    const confirmHandler = useCallback(() => {
        executeCall();
    }, [executeCall]);

    const removeHandler = useCallback(() => {
        executeRemove();
    }, [executeRemove]);

    const inputRef = useRef(null);
    const confirmButtonRef = useRef(null);
    const onKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && confirmButtonRef.current) {
            confirmButtonRef.current.click();
        }
    }, []);
    useEffect(() => {
        const inputElement = inputRef.current;
        if (inputElement) {
            inputElement.addEventListener('keydown', onKeyDown);
        }

        return () => {
            if (inputElement) {
                inputElement.removeEventListener('keydown', onKeyDown);
            }
        };
    }, [onKeyDown]);
    // buttons
    const ConfirmButton = useMemo(() => function () {
        if ([CALL_STATUS.PREFILLED, CALL_STATUS.SUCCESS].includes(callStatus)) return null;
        return (
            <Button
                htmlButtonType="button"
                type="primary"
                data-wdio="competitor-url-button"
                id="competitor-url-button"
                onClick={confirmHandler}
                disabled={loading || !competitorUrl || !!error}
                parentRef={confirmButtonRef}
            >
                {ctaConfirm}
            </Button>
        );
    }, [callStatus, confirmHandler, loading, competitorUrl, error, ctaConfirm]);

    const RemoveButton = useMemo(() => function () {
        if (![CALL_STATUS.PREFILLED, CALL_STATUS.SUCCESS].includes(callStatus)) return null;
        return (
            <Button
                htmlButtonType="button"
                type="utility"
                variant="utility-standard"
                data-wdio="competitor-url-button-remove"
                id="competitor-url-button-remove"
                onClick={removeHandler}
                disabled={loading}
            >
                {ctaCancel}
            </Button>
        );
    }, [callStatus, removeHandler, loading, ctaCancel]);
    const isDisabled = useCallback(() => [
        CALL_STATUS.SUCCESS,
        CALL_STATUS.LOADING,
        CALL_STATUS.PREFILLED
    ].includes(callStatus), [callStatus]);
    return (
        <div className={cn(
            'competitor-url-component',
            { 'competitor-url-component--loading': loading }
        )}
        >
            <Loader
                classes={cn('competitor-url-component__loader', {
                    'competitor-url-component__loader--loading': loading
                })}
                visible={loading}
            />
            <div className={cn('competitor-url-component__header')}>
                <p><b>{title}</b></p>
                <p>{description}</p>
            </div>
            <div className={cn('competitor-url-component__content')}>
                <div className={cn('competitor-url-component__input')}>
                    <Input
                        iconName={[CALL_STATUS.PREFILLED, CALL_STATUS.SUCCESS].includes(callStatus) ? 'check-circle' : ''}
                        type="text"
                        disabled={isDisabled()}
                        onChange={inputHandler}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        value={competitorUrl}
                        label={getInputLabel()}
                        floatLabel
                        invalid={!!error}
                        validationMessage={error}
                        parentRef={inputRef}
                        id="competitor-url-link-input"
                    />
                </div>
                <div className={cn('competitor-url-component__buttons')}>
                    <ConfirmButton />
                    <RemoveButton />
                </div>
            </div>
        </div>
    );
}

BaseComponent.propTypes = BaseComponentProptypes;

export default BaseComponent;
